import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Form, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import CrossIcon from "assets/img/file-cut-icon.png";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../../redux/slice/managePasswordSlice";
import { useNavigate } from "react-router-dom";
import { setActiveMenu } from "../../../../redux/slice/activeMenuSlice";
import { BiSolidHide, BiSolidShow } from "react-icons/bi";
import { BsCheckLg, BsXLg } from "react-icons/bs";
import PasswordCriteria from "utils/PasswordCriteria";
import { updateManagePassword } from "../../../../redux/slice/managePasswordApiSlice";
import {resetState} from "../../../../redux/slice/managePasswordApiSlice"
const PASSWORD_CRITERIA = PasswordCriteria();

const ValidationLine = ({ condition, text }) => (
  <div
    className={condition ? "text-success" : "text-danger"}
    style={{ fontSize: "0.8rem", lineHeight: "1.4" }}
  >
    {condition ? (
      <BsCheckLg style={{ marginRight: "5px" }} />
    ) : (
      <BsXLg style={{ marginRight: "5px" }} />
    )}{" "}
    {text}
  </div>
);

ValidationLine.propTypes = {
  condition: PropTypes.bool,
  text: PropTypes.string.isRequired,
};

function ManagePassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const [passwordShow, setPasswordShow] = useState(false);
  const [newPasswordShow, setNewPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);
  const [resetPasswordCriteriaMessage, setResetPasswordCriteriaMessage] =
    useState(false);
  const [criteriaStatus, setCriteriaStatus] = useState({});

  const { show, isForNewUser } = useSelector((state) => state.managePassword);
  const newPasswordValue = watch("newPassword");
  const { managePassword, status } = useSelector(
    (state) => state.managePasswordApi
  );
  const InitialPermissionData = useSelector(
    (state) => state.persistedReducer?.forms?.savedPermissions
  );

  const { userId } = InitialPermissionData ?? {};

  useEffect(() => {
    if (managePassword?.success === true) {
        dispatch(closeModal());

        if (isForNewUser) {
            dispatch(
              setActiveMenu({ menu: "myjoiningdetails", subMenu: "summary" })
            );
            dispatch(closeModal());
            navigate("/admin/my-details", { replace: true });
        }
    }
  }, [status, dispatch, isForNewUser, managePassword?.success, navigate]);

  useEffect(() => {
    if (newPasswordValue) {
      const newStatus = {};
      PASSWORD_CRITERIA.forEach((item) => {
        newStatus[item.key] = item.regex.test(newPasswordValue);
      });
      setCriteriaStatus(newStatus);
    } else {
      setCriteriaStatus({});
    }
  }, [newPasswordValue]);

  const isPasswordStrong = PASSWORD_CRITERIA.every(
    (item) => criteriaStatus[item.key]
  );

  const handleNewPasswordFocus = () => setResetPasswordCriteriaMessage(true);
  const handleNewPasswordBlur = () => setResetPasswordCriteriaMessage(false);

  const onSubmit = (data) => {
    if (!isPasswordStrong) {
      toast.error("New password does not meet all requirements!", {
        className: "toast-error-custom",
      });
      return;
    }
    dispatch(updateManagePassword({ userId, data }));
  };
  useEffect(() => {
  if (managePassword) {
    if (managePassword.success === true) {
      toast.success(managePassword.data || "Password updated successfully!", {
        className: "toast-success-custom",
      });

        dispatch(closeModal());
        if (isForNewUser) {
          dispatch(
              setActiveMenu({ menu: "myjoiningdetails", subMenu: "summary" })
            );
          navigate("/admin/my-details", { replace: true });
        }
      
      reset(); 
      dispatch(resetState());
    } 
    else if (managePassword.success === false) {
      toast.error(managePassword.data || "Something went wrong", {
        className: "toast-error-custom",
      });
      dispatch(resetState());
    }
  }
}, [managePassword, dispatch, isForNewUser, navigate, reset]);

  const handleOldPasswordVisibility = () => setPasswordShow(!passwordShow);
  const handleNewPasswordVisibility = () => {
    setNewPasswordShow((prev) => !prev);
  };

  const handleConfirmPasswordVisibility = () =>
    setConfirmPasswordShow(!confirmPasswordShow);

  return (
    <Modal show={show} backdrop="static" keyboard={false} onHide={() => { }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header>
          <Modal.Title>Change Password</Modal.Title>
          {isForNewUser === false && (
            <button
            type="button"
              onClick={() => {
                dispatch(closeModal());
                reset();
              }}
              className="modal-close"
            >
              <img
                src={CrossIcon}
                alt="Close"
                style={{ width: "20px", height: "20px" }}
              />
            </button>
          )}
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mt-3">
            <Form.Label>Old Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={passwordShow ? "text" : "password"}
                placeholder="Enter the old password"
                {...register("oldPassword", {
                  required: "Old password is required",
                })}
                isInvalid={errors.oldPassword}
                style={{cursor:'text'}}
              />
              <InputGroup.Text
                onClick={handleOldPasswordVisibility}
                className="cursor-pointer"
              >
                {passwordShow ? <BiSolidHide /> : <BiSolidShow />}
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.oldPassword?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={newPasswordShow ? "text" : "password"}
                placeholder="Enter the new password"
                {...register("newPassword", {
                  required: "New password is required",
                })}
                isInvalid={errors.newPassword}
                onFocus={handleNewPasswordFocus}
                onBlur={handleNewPasswordBlur}
                 style={{cursor:'text'}}
              />
              <InputGroup.Text
                onClick={handleNewPasswordVisibility}
                onMouseDown={(e) => e.preventDefault()}
                className="cursor-pointer"
              >
                {newPasswordShow ? <BiSolidHide /> : <BiSolidShow />}
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.newPassword?.message}
              </Form.Control.Feedback>
            </InputGroup>
            {resetPasswordCriteriaMessage && (
              <div
                className="password-criteria visible p-2 mt-2 border rounded"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <strong className="d-block mb-1">Password Requirements:</strong>
                {PASSWORD_CRITERIA.map((item) => (
                  <ValidationLine
                    key={item.key}
                    condition={criteriaStatus[item.key]}
                    text={item.message}
                  />
                ))}
              </div>
            )}

            <Form.Control.Feedback
              type="invalid"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                marginTop: resetPasswordCriteriaMessage ? "5px" : "0",
              }}
            >
              {errors.newPassword?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Confirm Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={confirmPasswordShow ? "text" : "password"}
                placeholder="Confirm the new password"
                {...register("confirmPassword", {
                  required: "Confirm Password is required",
                  validate: (value) =>
                    value === newPasswordValue || "Password does not match",
                })}
                isInvalid={errors.confirmPassword}
                 style={{cursor:'text'}}
              />
              <InputGroup.Text
                onClick={handleConfirmPasswordVisibility}
                className="cursor-pointer"
              >
                {confirmPasswordShow ? <BiSolidHide /> : <BiSolidShow />}
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer
          style={{
            justifyContent: isForNewUser === true ? "center" : "space-between",
          }}
        >
          {isForNewUser === false && (
            <Button variant="cancel-cta" onClick={() =>{ dispatch(closeModal());
               reset(); 
            }}>
              Cancel
            </Button>
          )}
          <Button variant="update-cta" type="submit">
            Change
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ManagePassword;
